import React, { useState, useEffect } from 'react';
import { LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const navItems = [
  { label: 'Home', id: 'hero' },
  { label: 'Courses', id: 'courses' },
  { label: 'Books', id: 'books' },
  { label: 'Apps', id: 'apps' },
  { label: 'Channels', id: 'channels' },
];

const Navbar = ({ onAuthOpen }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const handleAuthClick = (mode) => {
    onAuthOpen(mode);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    setMobileOpen(false);
  };

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '16px 0',
        background: scrolled || mobileOpen ? 'rgba(15, 10, 30, 0.95)' : 'transparent',
        backdropFilter: scrolled || mobileOpen ? 'blur(20px)' : 'none',
        borderBottom: scrolled || mobileOpen ? '1px solid rgba(139, 92, 246, 0.2)' : 'none',
        transition: 'background 0.3s ease, backdrop-filter 0.3s ease, border-bottom 0.3s ease',
      }}
    >
      <div className="navbar-inner">
        {/* Logo */}
        <div
          data-testid="nav-logo"
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
          onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileOpen(false); }}
        >
          <span style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '22px',
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}>
            <span style={{ color: '#00d4ff' }}>Inner</span>{' '}
            <span style={{ color: '#c084fc' }}>Bridge</span>
          </span>
        </div>

        {/* Desktop Nav Links */}
        <div className="nav-links-desktop">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-link-${item.id}`}
              onClick={() => scrollToSection(item.id)}
              style={{
                background: 'none',
                border: 'none',
                color: '#b0adc0',
                fontSize: '15px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
                padding: '4px 0',
                position: 'relative',
              }}
              onMouseEnter={(e) => { e.target.style.color = '#ffffff'; }}
              onMouseLeave={(e) => { e.target.style.color = '#b0adc0'; }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Desktop Auth */}
        <div className="nav-auth-desktop">
          {user ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {user.picture && (
                  <img
                    src={user.picture}
                    alt={user.name}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '2px solid rgba(139, 92, 246, 0.5)',
                    }}
                  />
                )}
                <span style={{
                  color: '#e0dff0',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: 'Inter, sans-serif',
                  maxWidth: '160px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {user.name}
                </span>
              </div>
              <button
                data-testid="nav-logout-btn"
                onClick={logout}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(139, 92, 246, 0.4)',
                  color: '#b0adc0',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <LogOut size={16} />
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="nav-login-btn"
                onClick={() => onAuthOpen('login')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#b0adc0',
                  fontSize: '15px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
                onMouseEnter={(e) => { e.target.style.color = '#ffffff'; }}
                onMouseLeave={(e) => { e.target.style.color = '#b0adc0'; }}
              >
                Log In
              </button>
              <button
                data-testid="nav-signup-btn"
                onClick={() => onAuthOpen('signup')}
                style={{
                  background: 'transparent',
                  border: '2px solid #ef4444',
                  color: '#ef4444',
                  padding: '8px 24px',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Sign Up
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          data-testid="nav-hamburger-btn"
          className="nav-hamburger"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'open' : ''}`} data-testid="mobile-menu">
        {navItems.map((item) => (
          <button
            key={item.id}
            data-testid={`mobile-nav-link-${item.id}`}
            onClick={() => scrollToSection(item.id)}
          >
            {item.label}
          </button>
        ))}

        <div style={{ height: 1, background: 'rgba(139, 92, 246, 0.2)', margin: '6px 0' }} />

        {user ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px' }}>
              {user.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: '2px solid rgba(139, 92, 246, 0.5)',
                  }}
                />
              )}
              <span style={{ color: '#e0dff0', fontSize: '14px', fontWeight: 500 }}>
                {user.name}
              </span>
            </div>
            <button data-testid="mobile-logout-btn" onClick={handleLogout} style={{ color: '#ef4444' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <LogOut size={16} /> Logout
              </span>
            </button>
          </>
        ) : (
          <>
            <button data-testid="mobile-login-btn" onClick={() => handleAuthClick('login')}>
              Log In
            </button>
            <button
              data-testid="mobile-signup-btn"
              onClick={() => handleAuthClick('signup')}
              style={{ color: '#ef4444', fontWeight: 600 }}
            >
              Sign Up
            </button>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
